import { useDeleteLot } from '@/api/hooks';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { getErrorMessage } from '@/lib/api';
import type { Lot } from '@/types/api';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';

export function DeleteLotModal({
  lot,
  open,
  onClose,
  onDeleted,
}: {
  lot: Lot | null;
  open: boolean;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const deleteLot = useDeleteLot();
  const [error, setError] = useState('');

  async function handleDelete() {
    if (!lot) return;
    setError('');
    try {
      await deleteLot.mutateAsync(lot.id);
      onClose();
      onDeleted?.();
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao excluir lote.'));
    }
  }

  const animalCount = lot?.active_animals_count ?? 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Excluir lote"
      size="md"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={deleteLot.isPending}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleDelete}
            loading={deleteLot.isPending}
          >
            Excluir lote
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-manejai-bark">
          Tem certeza que deseja excluir o lote{' '}
          <span className="font-medium text-manejai-forest">{lot?.name}</span>?
        </p>

        {animalCount > 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <div>
                <p className="font-medium">
                  Este lote possui {animalCount} animal(is) ativo(s) vinculado(s).
                </p>
                <p className="mt-2">
                  O lote será removido da listagem, mas os animais permanecem no rebanho com o vínculo
                  ao lote antigo. Recomendamos transferi-los antes da exclusão.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-manejai-bark/70">
            Não há animais ativos neste lote. Esta ação não pode ser desfeita.
          </p>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}
